import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Users, 
  PackageOpen, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  XCircle,
  Eye,
  Calendar,
  Settings
} from 'lucide-react';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import { Quotation } from '../../types';
import { useSettings } from '../../contexts/SettingsContext';
import toast from 'react-hot-toast';

const DashboardPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalQuotations: 0,
    draftQuotations: 0,
    sentQuotations: 0,
    acceptedQuotations: 0,
    rejectedQuotations: 0,
  });
  const [recentQuotations, setRecentQuotations] = useState<Quotation[]>([]);
  const { settings } = useSettings();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [quotationsRes] = await Promise.all([
        apiService.getQuotations({ limit: 1000, page: 1 }), // Get all for accurate stats
      ]);

      const quotations = quotationsRes.data.quotations || [];
      const pagination = quotationsRes.data.pagination;

      setStats({
        totalQuotations: pagination?.totalItems || quotations.length,
        draftQuotations: quotations.filter((q: Quotation) => q.status === 'draft').length,
        sentQuotations: quotations.filter((q: Quotation) => q.status === 'sent').length,
        acceptedQuotations: quotations.filter((q: Quotation) => q.status === 'accepted').length,
        rejectedQuotations: quotations.filter((q: Quotation) => q.status === 'rejected').length,
      });

      setRecentQuotations(quotations.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: 'Total Quotations', 
      value: stats.totalQuotations, 
      icon: FileText, 
      color: 'from-blue-500 to-blue-600', 
      textColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      link: '/quotations' 
    },
    { 
      title: 'Draft', 
      value: stats.draftQuotations, 
      icon: Clock, 
      color: 'from-gray-500 to-gray-600', 
      textColor: 'text-gray-600',
      bgColor: 'bg-gray-50',
      link: '/quotations?status=draft' 
    },
    { 
      title: 'Sent', 
      value: stats.sentQuotations, 
      icon: TrendingUp, 
      color: 'from-yellow-500 to-yellow-600', 
      textColor: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      link: '/quotations?status=sent' 
    },
    { 
      title: 'Accepted', 
      value: stats.acceptedQuotations, 
      icon: CheckCircle, 
      color: 'from-green-500 to-green-600', 
      textColor: 'text-green-600',
      bgColor: 'bg-green-50',
      link: '/quotations?status=accepted' 
    },
    { 
      title: 'Rejected', 
      value: stats.rejectedQuotations, 
      icon: XCircle, 
      color: 'from-red-500 to-red-600', 
      textColor: 'text-red-600',
      bgColor: 'bg-red-50',
      link: '/quotations?status=rejected' 
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
          Dashboard
        </h1>
        <p className="mt-2 text-sm text-gray-600" style={{ fontFamily: 'var(--font-family)' }}>
          Welcome to {settings?.companyName || 'The Aircons Ltd'} Quotation System
        </p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Link to={stat.link}>
                <Card hover padding="md" className="h-full">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600 mb-1" style={{ fontFamily: 'var(--font-family)' }}>
                        {stat.title}
                      </p>
                      <p className={`text-2xl font-bold ${stat.textColor}`} style={{ fontFamily: 'var(--font-family)' }}>
                        {stat.value}
                      </p>
                    </div>
                    <div className={`${stat.bgColor} rounded-lg p-3`}>
                      <Icon className={`h-6 w-6 ${stat.textColor}`} />
                    </div>
                  </div>
                </Card>
              </Link>
            </motion.div>
          );
        })}
      </div>

      {/* Recent Quotations */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        <Card padding="none">
          <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
                Recent Quotations
              </h3>
              <p className="mt-1 text-sm text-gray-500" style={{ fontFamily: 'var(--font-family)' }}>
                Your latest quotations at a glance
              </p>
            </div>
            <Link to="/quotations">
              <Button variant="ghost" size="sm">
                View All
              </Button>
            </Link>
          </div>
          
          {recentQuotations.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'var(--font-family)' }}>
                      Quotation #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'var(--font-family)' }}>
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'var(--font-family)' }}>
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'var(--font-family)' }}>
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ fontFamily: 'var(--font-family)' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentQuotations.map((quotation, index) => (
                    <motion.tr 
                      key={quotation._id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600" style={{ fontFamily: 'var(--font-family)' }}>
                        <Link to={`/quotations/${quotation._id}`}>{quotation.quotationNumber}</Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
                        {typeof quotation.customer === 'object' ? quotation.customer.name : quotation.customer}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" style={{ fontFamily: 'var(--font-family)' }}>
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(quotation.quotationDate).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`status-badge status-${quotation.status}`}>
                          {quotation.status.charAt(0).toUpperCase() + quotation.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <Link to={`/quotations/${quotation._id}`}>
                          <Button variant="ghost" size="sm" icon={<Eye className="w-4 h-4" />}>
                            View
                          </Button>
                        </Link>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-12 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
                No quotations yet
              </h3>
              <p className="mt-1 text-sm text-gray-500" style={{ fontFamily: 'var(--font-family)' }}>
                Get started by creating your first quotation from the Quotations page.
              </p>
            </div>
          )}
        </Card>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.4 }}
      >
        <h3 className="text-lg font-medium text-gray-900 mb-4" style={{ fontFamily: 'var(--font-family)' }}>
          Quick Actions
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Link to="/customers">
            <Card hover padding="md" className="text-center">
              <Users className="mx-auto h-8 w-8 text-green-600 mb-2" />
              <h4 className="text-sm font-medium text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
                Manage Customers
              </h4>
            </Card>
          </Link>
          <Link to="/quotation-items">
            <Card hover padding="md" className="text-center">
              <PackageOpen className="mx-auto h-8 w-8 text-purple-600 mb-2" />
              <h4 className="text-sm font-medium text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
                Catalog Items
              </h4>
            </Card>
          </Link>
          <Link to="/settings">
            <Card hover padding="md" className="text-center">
              <Settings className="mx-auto h-8 w-8 text-gray-600 mb-2" />
              <h4 className="text-sm font-medium text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
                Settings
              </h4>
            </Card>
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default DashboardPage;
